import { Component, OnInit, ViewChild, inject, ChangeDetectorRef } from '@angular/core';
import { CommonModule } from '@angular/common';
import { Table, TableModule } from 'primeng/table';
import { ButtonModule } from 'primeng/button';
import { InputTextModule } from 'primeng/inputtext';
import { DialogModule } from 'primeng/dialog';
import { ToastModule } from 'primeng/toast';
import { FormsModule, ReactiveFormsModule, FormBuilder, FormGroup, Validators } from '@angular/forms';
import { Select } from 'primeng/select';
import { MessageService } from 'primeng/api';
import { PaymentDefinitionsService } from '@services/payment-definitions.service';
import { IPspSettingType, ITypeListItem } from '@shared/interfaces/payment-definitions.interface';
import { ColumnFilterComponent } from '@app/shared/components/column-filter/column-filter.component';
import { IconField } from 'primeng/iconfield';
import { InputIcon } from 'primeng/inputicon';
import { PageHeaderComponent } from '@app/shared/components/page-header/page-header.component';

@Component({
    selector: 'app-psp-setting',
    imports: [
        CommonModule,
        TableModule,
        ButtonModule,
        InputTextModule,
        DialogModule,
        ToastModule,
        FormsModule,
        ReactiveFormsModule,
        Select,
        ColumnFilterComponent,
        IconField,
        InputIcon,
        PageHeaderComponent
    ],
    templateUrl: './psp-setting.component.html',
    styleUrl: './psp-setting.component.scss'
})
export class PspSettingComponent implements OnInit {
    @ViewChild('dt') dt!: Table;

    settings: IPspSettingType[] = [];
    pspTypes: ITypeListItem[] = [];
    loading = true;
    dialogVisible = false;
    isEditMode = false;
    selectedSetting: IPspSettingType | null = null;
    submitted = false;
    settingForm!: FormGroup;

    private paymentService = inject(PaymentDefinitionsService);
    private fb = inject(FormBuilder);
    private messageService = inject(MessageService);
    private cd = inject(ChangeDetectorRef);

    ngOnInit(): void {
        this.initializeForm();
        this.loadSettings();
        this.loadDropdowns();
    }

    onGlobalSearch(event: Event): void {
        const value = (event.target as HTMLInputElement).value;
        this.dt.filterGlobal(value, 'contains');
    }

    loadSettings(): void {
        this.loading = true;
        this.cd.detectChanges();
        this.paymentService.getSettings().subscribe({
            next: (res) => {
                this.settings = res.pspSettingTypes || [];
                this.loading = false;
                this.cd.detectChanges();
            },
            error: () => {
                this.loading = false;
                this.cd.detectChanges();
            }
        });
    }

    loadDropdowns(): void {
        this.paymentService.getPspTypeInformation().subscribe({
            next: (res) => {
                this.pspTypes = res.typeList || [];
            }
        });
    }

    initializeForm(): void {
        this.settingForm = this.fb.group({
            key: ['', [Validators.required]],
            value: ['', [Validators.required]],
            pspTypeId: [null, [Validators.required]]
        });
    }

    isInvalid(field: string): boolean {
        const control = this.settingForm.get(field);
        return !!(control && control.invalid && (this.submitted || control.dirty || control.touched));
    }

    openCreate(): void {
        this.isEditMode = false;
        this.selectedSetting = null;
        this.submitted = false;
        this.settingForm.reset({ key: '', value: '', pspTypeId: null });
        this.dialogVisible = true;
    }

    openEdit(setting: IPspSettingType): void {
        this.isEditMode = true;
        this.selectedSetting = setting;
        this.submitted = false;
        this.settingForm.patchValue({
            key: setting.key,
            value: setting.value,
            pspTypeId: setting.pspTypeId
        });
        this.dialogVisible = true;
    }

    cancelDialog(): void {
        this.dialogVisible = false;
    }

    saveSetting(): void {
        this.submitted = true;
        if (this.settingForm.invalid) {
            return;
        }

        const formData = this.settingForm.value;
        const request = {
            key: formData.key,
            value: formData.value,
            pspTypeId: formData.pspTypeId
        };

        if (!this.isEditMode) {
            this.paymentService.createSetting(request).subscribe({
                next: () => {
                    this.messageService.add({ severity: 'success', summary: 'Success', detail: 'Setting has been created successfully.' });
                    this.loadSettings();
                    this.dialogVisible = false;
                }
            });
        } else if (this.selectedSetting) {
            this.paymentService.updateSetting(this.selectedSetting.id, request).subscribe({
                next: () => {
                    this.messageService.add({ severity: 'success', summary: 'Success', detail: 'Setting has been updated successfully.' });
                    this.loadSettings();
                    this.dialogVisible = false;
                }
            });
        }
    }
}
